import { AssigneeJane24Image } from "../assets/image-assignee-jane";
import { AssigneeBlake24Image } from "../assets/image-assignee-blake";
import { AssigneeAlvin24Image } from "../assets/image-assignee-alvin";
import { PriorityMinor24Icon } from "../assets/icon-priority-minor";
import { PriorityHigh16Icon } from "../assets/icon-priority-high";
import { PriorityCritical24Icon } from "../assets/icon-priority-critical";
import { PriorityBlocker24Icon } from "../assets/icon-priority-blocker";
import { css } from "@compiled/react";

import Lozenge from "@atlaskit/lozenge";
import BugObject from "@atlaskit/object/bug";
import StoryObject from "@atlaskit/object/story";
import TaskObject from "@atlaskit/object/task";

import { Message } from "../constants/types";
import { Assignee, EpicName, IssueTypeName, Priority } from "../constants/ui";

const issueText = css({
    textTransform: "uppercase",
    marginInlineStart: "4px",
    fontSize: "11px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "16px"
});

export const getAssetOptionsForIssueType = (
    value: string | undefined,
    messages: Message,
    includeLabel: boolean
) => {
    const {
        createIssueModalIssueTypeAssetLabel1,
        createIssueModalIssueTypeAssetLabel2,
        createIssueModalIssueTypeAssetLabel3
    } = includeLabel
        ? messages
        : {
              createIssueModalIssueTypeAssetLabel1: "",
              createIssueModalIssueTypeAssetLabel2: "",
              createIssueModalIssueTypeAssetLabel3: ""
          };
    switch (value) {
        case IssueTypeName.TASK:
            return (
                <>
                    <TaskObject label={createIssueModalIssueTypeAssetLabel1} />
                    <div css={issueText} aria-hidden>
                        {messages.issueTypeTask}
                    </div>
                </>
            );
        case IssueTypeName.STORY:
            return (
                <>
                    <StoryObject label={createIssueModalIssueTypeAssetLabel2} />
                    <div css={issueText} aria-hidden>
                        {messages.issueTypeStory}
                    </div>
                </>
            );
        case IssueTypeName.BUG:
            return (
                <>
                    <BugObject label={createIssueModalIssueTypeAssetLabel3} />
                    <div css={issueText} aria-hidden>
                        {messages.issueTypeBug}
                    </div>
                </>
            );
        case IssueTypeName.NO_ISSUE:
            return <></>;
        default:
            console.warn(`Asset for issue type ${value} does not exist.`);
            return <TaskObject label={createIssueModalIssueTypeAssetLabel1} />;
    }
};
export const getAssetOptionsForPriority = (
    value: string | undefined,
    messages: Message,
    includeLabel: boolean
) => {
    const {
        createIssueModalPriorityAssetLabel1,
        createIssueModalPriorityAssetLabel2,
        createIssueModalPriorityAssetLabel3,
        createIssueModalPriorityAssetLabel4
    } = includeLabel
        ? messages
        : {
              createIssueModalPriorityAssetLabel1: "",
              createIssueModalPriorityAssetLabel2: "",
              createIssueModalPriorityAssetLabel3: "",
              createIssueModalPriorityAssetLabel4: ""
          };
    switch (value) {
        case Priority.HIGH:
            return (
                <PriorityHigh16Icon
                    label={createIssueModalPriorityAssetLabel1}
                />
            );
        case Priority.CRITICAL:
            return (
                <PriorityCritical24Icon
                    label={createIssueModalPriorityAssetLabel2}
                />
            );
        case Priority.BLOCKER:
            return (
                <PriorityBlocker24Icon
                    label={createIssueModalPriorityAssetLabel3}
                />
            );
        case Priority.MINOR:
            return (
                <PriorityMinor24Icon
                    label={createIssueModalPriorityAssetLabel4}
                />
            );
        default:
            console.warn(`Asset for priority ${value} does not exist.`);
            return (
                <PriorityMinor24Icon
                    label={createIssueModalPriorityAssetLabel1}
                />
            );
    }
};
export const getAssetOptionsForAssignee = (
    value: string | undefined,
    messages: Message,
    includeLabel: boolean
) => {
    const {
        createIssueModalAssigneeAssetLabel1,
        createIssueModalAssigneeAssetLabel2,
        createIssueModalAssigneeAssetLabel3
    } = includeLabel
        ? messages
        : {
              createIssueModalAssigneeAssetLabel1: "",
              createIssueModalAssigneeAssetLabel2: "",
              createIssueModalAssigneeAssetLabel3: ""
          };
    switch (value) {
        case Assignee.ALVIN:
            return (
                <AssigneeAlvin24Image
                    alt={createIssueModalAssigneeAssetLabel1}
                />
            );
        case Assignee.JANE:
            return (
                <AssigneeJane24Image
                    alt={createIssueModalAssigneeAssetLabel2}
                />
            );
        case Assignee.BLAKE:
            return (
                <AssigneeBlake24Image
                    alt={createIssueModalAssigneeAssetLabel3}
                />
            );
        default:
            console.warn(`Asset for assignee ${value} does not exist.`);
            return (
                <AssigneeAlvin24Image
                    alt={createIssueModalAssigneeAssetLabel1}
                />
            );
    }
};
export const getAssetForEpicLink = (
    value: string | undefined,
    messages: Message
) => {
    switch (value) {
        case EpicName.FEATURES:
            return (
                <Lozenge appearance={"inprogress"} isBold>
                    {messages.epicNameFeatures}
                </Lozenge>
            );
        case EpicName.ANALYTICS:
            return (
                <Lozenge appearance={"success"} isBold>
                    {messages.epicNameAnalytics}
                </Lozenge>
            );
        case EpicName.PAYMENTS:
            return (
                <Lozenge appearance={"new"} isBold>
                    {messages.epicNamePayments}
                </Lozenge>
            );
        case EpicName.ASSETS:
            return (
                <Lozenge appearance={"moved"} isBold>
                    {messages.epicNameAssets}
                </Lozenge>
            );
        case EpicName.STRATEGY:
            return (
                <Lozenge appearance={"inprogress"} isBold>
                    {messages.epicNameStrategy}
                </Lozenge>
            );
        case EpicName.BUDGET:
            return (
                <Lozenge appearance={"success"} isBold>
                    {messages.epicNameBudget}
                </Lozenge>
            );
        case EpicName.Q1_CAMPAIGN:
            return (
                <Lozenge appearance={"inprogress"} isBold>
                    {messages.epicNameQ1Campaign}
                </Lozenge>
            );
        default:
            console.warn(`Asset for epic link ${value} does not exist.`);
            return (
                <Lozenge appearance={"inprogress"} isBold>
                    {value}
                </Lozenge>
            );
    }
};
